/**
 * Feature Flags Page - Company Admin
 */
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Flag, Plus, ToggleLeft, ToggleRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import * as api from '@/lib/companyAdminApi';

export default function FeatureFlagsPage() {
  const { token } = useOutletContext();
  const [flags, setFlags] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [newFlag, setNewFlag] = useState({
    flag_key: '',
    description: '',
    default_enabled: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [flagsRes, restaurantsRes] = await Promise.all([
        api.getFeatureFlags(),
        api.getRestaurants(),
      ]);
      setFlags(flagsRes.data.items || []);
      setRestaurants(restaurantsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlag = async () => {
    try {
      await api.createFeatureFlag(newFlag);
      toast.success('Feature flag created');
      setShowCreateDialog(false);
      setNewFlag({ flag_key: '', description: '', default_enabled: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create flag');
    }
  };

  const handleToggleDefault = async (flagKey, currentValue) => {
    try {
      await api.setFlagDefault(flagKey, !currentValue);
      toast.success('Default value updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const handleSetOverride = async (flagKey, restaurantId, enabled) => {
    try {
      await api.setFlagOverride(flagKey, restaurantId, enabled);
      toast.success('Override set');
    } catch (error) {
      toast.error('Failed to set override');
    }
  };

  const openOverrideDialog = (flag) => {
    setSelectedFlag(flag);
    setShowOverrideDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="feature-flags-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          <p className="text-gray-500">Control feature rollouts per restaurant</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-flag-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Flag
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Flags</span>
              <Flag className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{flags.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Enabled by Default</span>
              <ToggleRight className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {flags.filter(f => f.default_enabled).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Disabled by Default</span>
              <ToggleLeft className="h-5 w-5 text-gray-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {flags.filter(f => !f.default_enabled).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Flags Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flag Key</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : flags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No feature flags yet
                  </TableCell>
                </TableRow>
              ) : (
                flags.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm">
                        {flag.flag_key}
                      </code>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {flag.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={flag.default_enabled}
                          onCheckedChange={() => handleToggleDefault(flag.flag_key, flag.default_enabled)}
                        />
                        <span className="text-sm">
                          {flag.default_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openOverrideDialog(flag)}
                      >
                        <Building2 className="h-4 w-4 mr-1" />
                        Overrides
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Feature Flag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Flag Key</label>
              <Input
                value={newFlag.flag_key}
                onChange={(e) => setNewFlag({ ...newFlag, flag_key: e.target.value })}
                placeholder="e.g., new_checkout_flow"
              />
              <p className="text-xs text-gray-500 mt-1">Use snake_case for consistency</p>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={newFlag.description}
                onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                placeholder="What does this flag control?"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Default Enabled</label>
              <Switch
                checked={newFlag.default_enabled}
                onCheckedChange={(v) => setNewFlag({ ...newFlag, default_enabled: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFlag}>Create Flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Restaurant Overrides: {selectedFlag?.flag_key}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {restaurants.map((restaurant) => (
              <div key={restaurant.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">{restaurant.name}</p>
                  <p className="text-xs text-gray-500">ID: {restaurant.id}</p>
                </div>
                <Switch
                  defaultChecked={selectedFlag?.default_enabled}
                  onCheckedChange={(enabled) => 
                    handleSetOverride(selectedFlag?.flag_key, restaurant.id, enabled)
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowOverrideDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
